"use client";
import cn from "clsx";
import { Box, Card, Text, Button, TextField } from "@radix-ui/themes";
import * as Label from "@radix-ui/react-label";
import * as Form from "@radix-ui/react-form";
import { useTranslations } from "next-intl";
import { LayoutProps } from "@/components/layout/layoutType";
import { useForm } from "react-hook-form";
import { getApiHostname } from "@/util/paramutil";
import { useContext, useEffect, useState } from "react";
import GoogleLoginButton from "./googleLogin";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { fetchHost, fetchUtil } from "@/util/fetchutil";
import { Debug } from "@/util/debug";
import { AlertContext } from "@/components/context/alertContext";
import AppleLoginButton from "./appleLogin";
import { alertLoading } from "../list/defaultType";
import { checkLoginCommon, loginFailed, loginSuccess } from "./signin";

// const signin_google_link =
//   "https://accounts.google.com/o/oauth2/auth?client_id=" +
//   process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID +
//   "&redirect_uri=" +
//   process.env.NEXT_PUBLIC_GOOGLE_CLIENT_CALLBACK +
//   "&response_type=code&scope=https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile";

export default function AdminSignin({ params }: { params?: LayoutProps }) {
  const { register, handleSubmit } = useForm();
  const ta = useTranslations("alert");
  const alertContext = useContext(AlertContext);

  const alert_p = alertContext.setProp;

  const checkLogin = (getData: any) => checkLoginCommon(ta, params?.locale, alert_p, getData);

  const onSubmit = async (data: any) => {
    //console.log(data);
    // const saltRounds = 10;
    // const salt = await bcrypt.genSalt(saltRounds);
    // const password = await bcrypt.hash(data.password, salt);
    if (data.userid.trim() === "" || data.password.trim() === "") {
      return;
    }

    if (alert_p) {
      alert_p(alertLoading);
    }

    fetchHost(
      "/api/sign/signin",
      {
        mode: "cors",
        method: "POST",
        headers: { "Content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id: data.userid,
          user_pass: data.password,
        }),
      },
      undefined,
      (getData: any) => {
        Debug(getData);
        checkLogin(getData);
      },
    );

    fetch(getApiHostname() + "/api/sign/signin", {
      mode: "cors",
      method: "POST",
      headers: { "Content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        user_id: data.userid,
        user_pass: data.password,
      }),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error("network error : " + res.status);
        }
      })
      .then((getData) => {
        Debug(getData);
        checkLogin(getData);
      })
      .catch((error) => {
        //setOpen(true);
      });
  };

  return (
    <Box maxWidth="720px" className="m-auto min-w-[80%] lg:min-w-[50%]">
      {params != null && params.user != null ? (
        <div className="flex flex-col p-6 text-center">
          <h3 className="whitespace-nowrap tracking-tight text-2xl font-bold" data-id="3">
            {ta("signin_already")}
          </h3>
        </div>
      ) : (
        <Card className={cn("text-card-foreground shadow-sm my-12")}>
          <Form.Root className="FormRoot" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col p-6">
              <h3 className="whitespace-nowrap tracking-tight text-2xl font-bold px-[13px]" data-id="3">
                {ta("signin")}
              </h3>
              <p className="text-sm text-muted-foreground px-[13px]" data-id="4">
                {ta("signin_text")}
                {/* Please enter your user id and password. */}
              </p>
              <Form.Field className="FormField px-[13px]" name="userid">
                <div className="space-y-2 mt-8">
                  <Form.Label asChild>
                    <Label.Root className="LabelRoot text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {ta("user_id")}
                    </Label.Root>
                  </Form.Label>
                  <Form.Control asChild>
                    <TextField.Root
                      className="Inputflex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      type="text"
                      placeholder={ta("insert_user_id")}
                      defaultValue=""
                      {...register("userid")}
                      autoComplete="on"
                    ></TextField.Root>
                  </Form.Control>
                </div>
              </Form.Field>
              <Form.Field className="FormField px-[13px]" name="password">
                <div className="space-y-2 mt-4">
                  <Form.Label asChild>
                    <Label.Root className="LabelRoot text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {ta("password")}
                    </Label.Root>
                  </Form.Label>
                  <Form.Control asChild>
                    <TextField.Root
                      className="Input flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      type="password"
                      {...register("password")}
                      placeholder={ta("insert_password")}
                      defaultValue=""
                      autoComplete="off"
                    ></TextField.Root>
                  </Form.Control>
                </div>
              </Form.Field>
              <Form.Submit asChild className=" ">
                <Button variant="solid" type="submit" size="3" className="p-3 cursor-pointer mt-4 mb-3 mx-[13px]">
                  <Text weight="bold">{ta("signin")}</Text>
                </Button>
              </Form.Submit>
              <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? ""}>
                <GoogleLoginButton loginFn={checkLogin} />
              </GoogleOAuthProvider>
              <AppleLoginButton loginFn={checkLogin} />
            </div>
          </Form.Root>
        </Card>
      )}
    </Box>
  );
}
